// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useRef, useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from 'react-router-dom';
import { AppProvider, useApp } from './store/AppContext.js';
import { LoginForm } from './components/auth/LoginForm.js';
import { RegisterForm } from './components/auth/RegisterForm.js';
import { RecoveryForm } from './components/auth/RecoveryForm.js';
import { BiometricLockScreen } from './components/auth/BiometricLockScreen.js';
import { MainApp } from './pages/MainApp.js';
import { initNativeShell } from './native/shell.js';
import { useNativeBackButton } from './native/useNative.js';

// Vite worker import
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import CryptoWorker from './workers/crypto.worker.ts?worker';

class AppErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <h2 className="text-xl font-semibold text-red-700 mb-2">Something went wrong</h2>
            <p className="text-gray-600 text-sm mb-4">{this.state.error.message}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { state } = useApp();
  if (!state.auth.isAuthenticated) {
    const to = state.auth.sessionExpired ? '/login?expired=1' : '/login';
    return <Navigate to={to} replace />;
  }
  return <>{children}</>;
}

function RedirectIfAuth({ children }: { children: React.ReactNode }) {
  const { state } = useApp();
  if (state.auth.isAuthenticated) {
    return <Navigate to="/accounts" replace />;
  }
  return <>{children}</>;
}

function Root() {
  const { state } = useApp();
  const { auth } = state;
  const navigate = useNavigate();

  // Native shell chrome + Android hardware back button (no-ops on web).
  useEffect(() => {
    void initNativeShell();
  }, []);
  useNativeBackButton();

  const workerRef = useRef<Worker | null>(null);
  const [workerReady, setWorkerReady] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    workerRef.current = new CryptoWorker() as Worker;
    setWorkerReady(true);
    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  // Neutral dark loading gate until the crypto worker is up AND the async
  // session bootstrap has resolved — avoids flashing the login form before a
  // stored session is restored (web) or the lock screen is shown (native).
  if (!workerReady || !workerRef.current || !auth.bootstrapped) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg text-text-muted">
        Loading…
      </div>
    );
  }

  // Native cold start with a biometric-gated session: present the lock screen.
  if (auth.locked) {
    return <BiometricLockScreen />;
  }

  const syncUrl = auth.syncUrl;
  const worker = workerRef.current;

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuth>
            <LoginForm
              syncUrl={syncUrl}
              worker={worker}
              onSwitchToRegister={() => navigate('/register')}
              onSwitchToRecover={() => navigate('/recover')}
            />
          </RedirectIfAuth>
        }
      />
      <Route
        path="/register"
        element={
          <RedirectIfAuth>
            <RegisterForm
              syncUrl={syncUrl}
              worker={worker}
              onSwitchToLogin={() => navigate('/login')}
            />
          </RedirectIfAuth>
        }
      />
      <Route
        path="/recover"
        element={
          <RedirectIfAuth>
            <RecoveryForm
              syncUrl={syncUrl}
              worker={worker}
              onSwitchToLogin={() => navigate('/login')}
            />
          </RedirectIfAuth>
        }
      />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <MainApp />
          </RequireAuth>
        }
      />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AppErrorBoundary>
        <AppProvider>
          <Root />
        </AppProvider>
      </AppErrorBoundary>
    </BrowserRouter>
  );
}
